import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSignature } from "lucide-react";
import { toast } from "sonner";
import NamingPieces, { PIECE_LABELS } from "@/components/admin/NamingPieces";
import ApplyNamingToProject from "@/components/admin/ApplyNamingToProject";

const DEFAULT_KEY = "__default__";
const SEP_LABELS = { "_": "Underscore  _", "-": "Dash  -", " ": "Space" };

const sample = (part, sep, customer) => {
  const clean = (s) => s.replace(/[^A-Za-z0-9]+/g, sep);
  const d = new Date();
  return {
    site_name: clean("Oak Hill"),
    site_number: "TX1234",
    description: clean("Wide Pan"),
    date: `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`,
    customer: clean(customer || "Pano"),
  }[part];
};

// Admin: how new photo/video files are named, per customer (falls back to the company default).
export default function PhotoNamingCard() {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(DEFAULT_KEY);
  const [parts, setParts] = useState([]);
  const [sep, setSep] = useState("_");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/naming-patterns").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load naming settings"));
  useEffect(() => { load(); }, []);

  const customer = target === DEFAULT_KEY ? "" : target;
  const own = useMemo(() => data?.patterns.find((p) => p.customer_lc === customer.toLowerCase()), [data, customer]);
  const companyDefault = data?.patterns.find((p) => p.customer_lc === "") || data?.builtin;
  useEffect(() => {
    if (!data) return;
    const p = own || companyDefault;
    setParts(p.parts);
    setSep(p.separator);
  }, [data, own, companyDefault]);

  if (!data) return null;
  const preview = `${parts.map((p) => sample(p, sep, customer)).join(sep) || "PHOTO"}.jpg`;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/naming-patterns", { customer, parts, separator: sep });
      toast.success("Naming saved — applies to new uploads");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try {
      await api.delete("/naming-patterns", { params: { customer } });
      toast.success(customer ? `${customer} now uses the company default` : "Company default reset");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset");
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="photo-naming-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <FileSignature className="w-5 h-5 text-sky-400" /> Photo naming
        </CardTitle>
        <p className="text-xs text-slate-400">Order the pieces used to name photos and videos. Applies to new uploads only.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Applies to</div>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="bg-slate-950 border-slate-700" data-testid="naming-customer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={DEFAULT_KEY}>All customers (company default)</SelectItem>
                {data.customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customer && (
              <div className="text-[11px] mt-1 text-slate-500" data-testid="naming-source">
                {own ? "Has its own pattern" : "Using the company default"}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Between pieces</div>
            <Select value={sep} onValueChange={setSep}>
              <SelectTrigger className="bg-slate-950 border-slate-700" data-testid="naming-separator-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {data.separators.map((s) => <SelectItem key={s} value={s}>{SEP_LABELS[s] || s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <NamingPieces parts={parts} allParts={data.parts} onChange={setParts} />
        <div className="rounded-md bg-slate-950 border border-slate-800 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Example</div>
          <div className="font-mono text-sm text-emerald-300 break-all" data-testid="naming-preview">{preview}</div>
          <div className="text-[10px] text-slate-500 mt-1">Extra photos in the same slot get {sep === " " ? "a space and" : `"${sep}"`} 2, 3…</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy || !parts.length} className="bg-sky-600 hover:bg-sky-500" data-testid="naming-save">Save</Button>
          {(own || (!customer && data.patterns.some((p) => p.customer_lc === ""))) && (
            <Button variant="outline" onClick={reset} disabled={busy} className="border-slate-700" data-testid="naming-reset">
              {customer ? "Use company default" : `Reset to ${data.builtin.parts.map((p) => PIECE_LABELS[p]).join(" + ")}`}
            </Button>
          )}
        </div>
        <ApplyNamingToProject />
      </CardContent>
    </Card>
  );
}
